/**
 * Applies the staff-editable ProEpoxy catalogue (GET /api/proepoxy/catalogue)
 * onto the static productData objects IN PLACE, so every hook/step that
 * imports EPOXY_PRIMERS, TOP_COAT_PRODUCTS, etc. picks up live pack sizes,
 * coverage and prices without a code change. Call `loadProEpoxyCatalogue()`
 * once before rendering the order builder.
 */
import API_URL from '@/config/api';
import {
  EPOXY_PRIMERS, DESIGN_COAT_PRODUCTS, PIGMENTS_PRODUCTS, TOP_COAT_PRODUCTS,
  HIGH_BUILD_EPOXY, FLAKE_PRODUCTS, POLYASPARTIC_SEAL,
} from './productData';

const replacePacks = (target) => (packs) => { target.packs = packs.map((p) => ({ ...p })); };

const APPLIERS = {
  primer_1_part: replacePacks(EPOXY_PRIMERS['1_part']),
  primer_2_part: replacePacks(EPOXY_PRIMERS['2_part']),
  design_comfort: replacePacks(DESIGN_COAT_PRODUCTS.comfort),
  design_crystal: (packs) => { Object.assign(DESIGN_COAT_PRODUCTS.crystal, packs[0] || {}); },
  pigment_paste: replacePacks(PIGMENTS_PRODUCTS.pigmentPaste),
  glitter: replacePacks(PIGMENTS_PRODUCTS.glitter),
  topcoat_polyurethane: replacePacks(TOP_COAT_PRODUCTS.polyurethane),
  topcoat_polyaspartic: replacePacks(TOP_COAT_PRODUCTS.polyaspartic),
  high_build_epoxy: replacePacks(HIGH_BUILD_EPOXY),
  flake: replacePacks(FLAKE_PRODUCTS),
  polyaspartic_seal: replacePacks(POLYASPARTIC_SEAL),
};

export function applyCatalogue(groups) {
  Object.entries(groups || {}).forEach(([key, group]) => {
    if (APPLIERS[key] && Array.isArray(group.packs)) APPLIERS[key](group.packs);
  });
}

let pending = null;

export async function fetchProEpoxyCatalogue() {
  const res = await fetch(`${API_URL}/api/proepoxy/catalogue`, { credentials: 'include' });
  if (!res.ok) throw new Error('Could not load ProEpoxy catalogue');
  return (await res.json()).groups;
}

// Idempotent: the builder can call this on every mount; the network hit
// happens once per page load, and edits made by staff land on next reload.
export function loadProEpoxyCatalogue({ force = false } = {}) {
  if (!pending || force) {
    pending = fetchProEpoxyCatalogue()
      .then((groups) => { applyCatalogue(groups); return groups; })
      .catch((e) => { pending = null; throw e; });
  }
  return pending;
}
